import { APP_STAGED, FAQ_DETAIL_SCREEN } from '../constants';

export const FAQ_UNAVAILABLE_MESSAGE = 'This FAQ is no longer available. Please choose another FAQ.';

const isActive = value => value === true || value === 1;
const hasText = value => typeof value === 'string' && value.trim().length > 0;
const isFaqId = value => Number.isSafeInteger(value) && value > 0;

export const isSelectableSliderFaq = faq => Boolean(
  faq && isFaqId(faq.id) && isActive(faq.active) && hasText(faq.question) &&
  (faq.language_code === 'ind' || faq.language_code === 'eng') &&
  faq.category && isActive(faq.category.active) && hasText(faq.category.name) &&
  hasText(faq.category.slug) && faq.category.slug !== '.' && faq.category.slug !== '..'
);

export const getSavedSliderFaqId = data => (
  data && data.type === FAQ_DETAIL_SCREEN && isFaqId(data.payload) &&
  data.body && data.body.id === data.payload ? data.payload : null
);

// Keep in sync with fe-marketplace-next/features/helps/shared/utils/faq-slug.ts.
// Category-page hashes use the question slug, NOT the id-prefixed detail route.
export const getSliderFaqQuestionSlug = question => {
  const slug = question.toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 150);
  if (slug) return slug;

  let hash = 0;
  for (let i = 0; i < question.length; i++) {
    hash = (hash * 31 + question.charCodeAt(i)) | 0;
  }
  return `faq-${Math.abs(hash).toString(36)}`;
};

export const buildSliderFaqDestination = (faq, frontendWeb, stage = APP_STAGED) => {
  if (!isSelectableSliderFaq(faq)) throw new Error(FAQ_UNAVAILABLE_MESSAGE);

  const invalidConfigMessage = 'The Help Center URL configuration is invalid. Contact an administrator.';
  if (!hasText(frontendWeb) || frontendWeb.trim().startsWith('/')) {
    throw new Error(invalidConfigMessage);
  }
  let origin;
  try {
    const configuredUrl = frontendWeb.trim();
    // Like SliderForm's existing URL normalizer, accept a host without a scheme.
    const url = configuredUrl.includes('://')
      ? configuredUrl
      : `http${stage === 'production' ? 's' : ''}://${configuredUrl}`;
    origin = new URL(url);
  } catch (error) {
    throw new Error(invalidConfigMessage);
  }
  if (!['http:', 'https:'].includes(origin.protocol) || origin.username || origin.password) {
    throw new Error(invalidConfigMessage);
  }
  const prefix = faq.language_code === 'eng' ? '/en' : '';
  return {
    data: {
      type: FAQ_DETAIL_SCREEN,
      payload: faq.id,
      body: {
        id: faq.id,
        category: faq.category.name,
        language_code: faq.language_code,
      },
    },
    redirect_url: `${origin.origin}${prefix}/helps/${encodeURIComponent(faq.category.slug)}#${getSliderFaqQuestionSlug(faq.question)}`,
  };
};

export const getSliderFaqError = error => {
  const response = error && error.response;
  const status = response && response.status;
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to access FAQs.';
  if (status === 404) return FAQ_UNAVAILABLE_MESSAGE;
  if (status === 429) return 'Too many requests. Please wait before trying again.';
  if (status === 422) {
    const data = response.data || {};
    const validation = data.error || data;
    const messages = validation.errors && typeof validation.errors === 'object'
      ? Object.values(validation.errors).reduce((all, value) => all.concat(value), []).filter(hasText)
      : [];
    return messages.join(' ') || (hasText(validation.message) ? validation.message : 'Unable to validate the FAQ selection. Please try again.');
  }
  return 'Could not load FAQs. Check your connection and try again.';
};
